import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ForgotPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIsSuccess(true);
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-modal">
      <div className="bg-surface rounded-trading shadow-trading-modal max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-heading-semibold text-text-primary">
              Reset Password
            </h3>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-secondary-50 rounded-trading transition-colors duration-trading-fast"
            >
              <Icon name="X" size={20} className="text-text-secondary" />
            </button>
          </div>

          {!isSuccess ? (
            <>
              <p className="text-text-secondary font-body-normal mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-body-medium text-text-primary mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Mail" size={18} className="text-text-secondary" />
                    </div>
                    <input
                      id="resetEmail"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      className={`w-full pl-10 pr-4 py-3 border rounded-trading bg-surface font-body-normal text-sm placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-trading-fast ${
                        error ? 'border-error' : 'border-border'
                      }`}
                      placeholder="Enter your email"
                      disabled={isLoading}
                    />
                  </div>
                  {error && (
                    <p className="mt-1 text-sm text-error font-body-normal">{error}</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-4 py-3 border border-border rounded-trading font-body-medium text-sm text-text-primary hover:bg-secondary-50 transition-colors duration-trading-fast"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-primary text-white px-4 py-3 rounded-trading font-body-medium text-sm hover:bg-primary-600 transition-colors duration-trading-fast disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-success-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h4 className="text-lg font-heading-semibold text-text-primary mb-2">
                Reset Link Sent
              </h4>
              <p className="text-text-secondary font-body-normal mb-6">
                We've sent a password reset link to <strong>{email}</strong>. 
                Please check your email and follow the instructions.
              </p>
              <button
                onClick={handleClose}
                className="w-full bg-primary text-white px-4 py-3 rounded-trading font-body-medium text-sm hover:bg-primary-600 transition-colors duration-trading-fast"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;